/**
 * Password reset for arcano.app
 * This code is under copyright Arcano Gmbh (https://arcano.app).
 *
 * - no commercial use
 * - no modifications
 * - no redistribution
 *
 * without written consent of Arcano Gmbh, zentralstrasse 47, 8003 zürich, switzerland, https://arcano.app
*/


import {
    disableWarningOnPageUnload,
    enableWarningOnPageUnload, getItem, hideAllBootboxes,
    setItem,
    showFlash,
    showPasswordErrorPrompt, showReencryptFilesPrompt
} from "./utils";
import {
    addNewUser,
    convertStringToArrayBufferView,
    generateScryptHash,
    getRsaAlgorithm, getSecretKey,
    sendEditedFormData
} from "./crypto";



export function setupResetPassword(db: IDBDatabase) {

    const form = document.getElementById('passwordResetForm') as HTMLFormElement;
    if (!form) {
        return
    }
    console.log('reset')

    const currentPassword = document.getElementById('current_password') as HTMLInputElement;
    if (!currentPassword) {
        return;
    }
    const repeatPassword = document.getElementById('repeat_password') as HTMLInputElement;
    const newPassword = document.getElementById('password') as HTMLInputElement;
    if (!currentPassword || !repeatPassword || !newPassword) {
        return;
    }
    // reset
    form.addEventListener('submit', async (event) => {
        event.preventDefault()

        //early termination scenarios

        if (repeatPassword.value !== newPassword.value) {
            showFlash('Passwords must match', 'info')
            return;
        }
        if (newPassword.value === '') {
            showFlash('Password cannot be empty', 'info')
            return;
        }
        if (currentPassword.value === newPassword.value) {
            showFlash('Identical Passwords', 'info')
            return;
        }

        const currentPasswordValue = new TextEncoder().encode(currentPassword.value);
        const newPasswordValue = new TextEncoder().encode(newPassword.value);
        const prelogin = await fetch('/pre_login').then((response) => response.json())
        const currentPasswordData = await generateScryptHash(currentPasswordValue, prelogin.hkdfSalt, prelogin.scryptSalt)
        const newPasswordData = await generateScryptHash(newPasswordValue)


        /**
         * Re encrypt secrets
         */
        async function reEncrypt(wrappedKeyOld: CryptoKey): Promise<void> {

            async function callback() {
                enableWarningOnPageUnload();

                const response_info = await fetch('/files', {
                    method: "POST",
                    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
                    // headers: {"Content-type": "application/www-form-urlencoded"},
                    headers: {
                        'Content-Type': 'application/json'

                    },
                    body: JSON.stringify({'current_password': currentPasswordData.scryptHashedPassword}) // b
                });


                if (response_info.status === 401) {

                    disableWarningOnPageUnload();
                    showPasswordErrorPrompt(() => {
                        hideAllBootboxes()
                    });
                    return;
                }
                if (response_info.status !== 200) {
                    hideAllBootboxes()
                    throw Error('Something went wrong')
                }

                const files = await response_info.json();

                const newUserData = await addNewUser(newPassword.value)

                const publicKey = JSON.parse(newUserData.publicKey);
                const rsaAlgo = getRsaAlgorithm();

                const newPublicKey = await window.crypto.subtle.importKey('jwk',
                    publicKey,
                    rsaAlgo,
                    false,
                    publicKey.key_ops);


                const enc = new TextEncoder();
                const passwordKey = await window.crypto.subtle.importKey(
                    "raw",
                    enc.encode(newPassword.value),
                    {name: "PBKDF2"},
                    false,
                    ["deriveBits", "deriveKey"]
                );

                const wrappingKey = await window.crypto.subtle.deriveKey(
                    {
                        "name": "PBKDF2",
                        salt: newUserData.salt,
                        "iterations": 100000,
                        "hash": "SHA-256"
                    },
                    passwordKey,
                    {"name": "AES-GCM", "iv": newUserData.iv, "length": 256} as AesDerivedKeyParams,
                    true,
                    ["wrapKey", "unwrapKey"]
                );
                const unwrappedKey = await crypto.subtle.unwrapKey("pkcs8",
                    newUserData.privateKey,
                    wrappingKey, {
                        name: "AES-GCM",
                        iv: newUserData.iv
                    }, rsaAlgo, false, ['decrypt']);


                let newFiles: any = {};
                let i = 0;
                const nFiles = Object.keys(files).length;
                for (let key in files) {
                    i += 1;
                    // check if the property/key is defined in the object itself, not in parent
                    if (files.hasOwnProperty(key)) {

                        let file = files[key];
                        // decrypt
                        const decryptedSecret = await window.crypto.subtle.decrypt(
                            wrappedKeyOld.algorithm,
                            wrappedKeyOld, //from generateKey or importKey above
                            convertStringToArrayBufferView(atob(file.key))
                        );
                        let secret = new TextDecoder("utf-8").decode(decryptedSecret);

                        //re-encrypt
                        const reEncryptedSecret = await window.crypto.subtle.encrypt({
                                name: "RSA-OAEP",
                                iv: newUserData.iv,
                            },
                            newPublicKey, //from generateKey or importKey above
                            convertStringToArrayBufferView(secret)
                        );

                        //decrypt for testing purposes
                        const decrypted = await window.crypto.subtle.decrypt({
                                name: "RSA-OAEP",
                                iv: newUserData.iv
                            },
                            unwrappedKey, //from generateKey or importKey above
                            // convertStringToArrayBufferView(atob(reEncryptedSecret))
                            reEncryptedSecret
                        );
                        const newEncryptedSecret = new TextDecoder("utf-8").decode(decrypted)

                        if (secret !== newEncryptedSecret) {
                            throw Error('Re-Encryption failed')
                        }


                        newFiles[key] = {
                            key: new Uint8Array(reEncryptedSecret)
                        };

                        const progressBar = document.getElementById('re-encrypt-progress');
                        if(progressBar) {
                            const value = (i / nFiles * 100).toString()
                            progressBar.setAttribute('aria-valuenow', value);
                            progressBar.style.width = value + '%';
                        }
                    }
                }

                let update = new FormData(form);
                update.delete('repeat_password')
                update.set('files', JSON.stringify(newFiles));
                update.set('current_password', currentPasswordData.scryptHashedPassword);
                update.set('password', newPasswordData.scryptHashedPassword);
                update.set('public_key', newUserData.publicKey);
                update.set('encrypted_private_key', (new Uint8Array(newUserData.privateKey)).toString());
                update.set('iv', newUserData.iv.toString());
                update.set('salt', newUserData.salt.toString());
                update.set("hkdf_salt", newPasswordData.hkdfSaltHex);
                update.set("scrypt_salt", newPasswordData.scryptSaltHex);
                update.set("scrypt_hashed_password", newPasswordData.scryptHashedPassword);

                //
                function callback(request: XMLHttpRequest, response: any) {
                    if (response.status === 200) {
                        // store in indexedDB
                        const onerror = () => {
                            showFlash('Error', 'danger')
                        }
                        const oncomplete = () => {
                            showFlash('Files are re-encrypted and new password is set', 'success')
                        }
                        setItem(db, 'key', unwrappedKey, oncomplete, onerror);
                        disableWarningOnPageUnload();
                        currentPassword.value = ''
                        repeatPassword.value = ''
                        newPassword.value = ''
                    }
                }

                sendEditedFormData('/files/update', update, 'PUT', callback)
            }

            const modal = showReencryptFilesPrompt()
            await callback()
            modal.hide();
        }

        const oncomplete = async (ev: any) => {
            let wrappedKeyOld = ev.target.result;
            // let wrappedKeyOld = event.target.result;
            // warn user what could go wrong
            await reEncrypt(wrappedKeyOld);
        }
        const onerror = async () => {
            // idem to above
        }
        async function reencrypt() {
            let key = await getSecretKey(currentPassword.value);
            if (key) {
                await reEncrypt(key);
            } else {
                showPasswordErrorPrompt()
            }
        }
        if(db) {
            getItem(db, 'key', oncomplete, onerror)
        } else {

            await reencrypt()
        }
    });
}